import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate, useLocation } from 'react-router-dom';
import { useSubscriptionAccess } from '@/hooks/useSubscriptionAccess';
import { SubscriptionPaywall } from '@/components/subscription/SubscriptionPaywall';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { 
    hasAccess, 
    shouldRedirect, 
    allowedPath, 
    loading: subscriptionLoading,
    gracePeriodInfo,
    isExpired
  } = useSubscriptionAccess();
  const location = useLocation();

  // Check if on subscription pages (always allowed)
  const isOnSubscriptionPage = location.pathname.startsWith('/subscription');

  // Auth loading state
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="text-sm text-muted-foreground">Проверка доступа...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to auth
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Subscription loading state (only show if auth is done)
  if (subscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="text-sm text-muted-foreground">Проверка подписки...</p>
        </div>
      </div>
    );
  }

  // If on subscription page, always allow access (so user can pay)
  if (isOnSubscriptionPage) {
    return <>{children}</>;
  }

  // Subscription expired and grace period ended - show paywall
  if (isExpired && gracePeriodInfo?.shouldForcePayment) {
    return <SubscriptionPaywall daysOverdue={gracePeriodInfo.daysOverdue} />;
  }

  // Should redirect to payment page
  if (shouldRedirect && !hasAccess) {
    return <Navigate to={allowedPath} replace />;
  }

  return <>{children}</>;
};
